import angular from 'angular';

import apiServiceModule from './APIService';
import logServiceModule from './LogService';
import { assign, isSensitiveKey, redactSensitiveValues, REDACTED } from '../redact';

const analyticsPublishPath = '/log-capture';
const logPublishIntervalSecs = 5;

// From Monolog
// TODO: Add log level support! Currently these are unused.
//
// const DEBUG = 100;
//
// /**
//  * Interesting events
//  *
//  * Examples: User logs in, SQL logs.
//  */
// const INFO = 200;
//
// /**
//  * Uncommon events
//  */
// const NOTICE = 250;
//
// /**
//  * Exceptional occurrences that are not errors
//  *
//  * Examples: Use of deprecated APIs, poor use of an API,
//  * undesirable things that are not necessarily wrong.
//  */
// const WARNING = 300;
//
// /**
//  * Runtime errors
//  */
// const ERROR = 400;
//
// /**
//  * Critical conditions
//  *
//  * Example: Application component unavailable, unexpected exception.
//  */
// const CRITICAL = 500;
//
// /**
//  * Action must be taken immediately
//  *
//  * Example: Entire website down, database unavailable, etc.
//  * This should trigger the SMS alerts and wake you up.
//  */
// const ALERT = 550;
//
// /**
//  * Urgent alert.
//  */
// const EMERGENCY = 600;


export const DEBUG = 100;
export const INFO = 200;
export const NOTICE = 250;
export const WARNING = 300;
export const ERROR = 400;
export const CRITICAL = 500;
export const ALERT = 550;
export const EMERGENCY = 600;

export const logLevels = {
  DEBUG,
  INFO,
  NOTICE,
  WARNING,
  ERROR,
  CRITICAL,
  ALERT,
  EMERGENCY,
};

export class InternalAnalyticsPublishingService {
  constructor(
    $injector,
    $interval,
    $q,
    APIService,
    LogService
  ) {
    this.$injector = $injector;
    this.$interval = $interval;
    this.$q = $q;
    this.APIService = APIService;
    this.LogService = LogService;
    this._DataSyncService = null;

    this._queue = [];
    this._attributes = {};
    this._needsPublish = false;
    this._publishTimer = null;
    this._publishError = false;
  }

  get isDataSyncInProgress() {
    if (this.DataSyncService) {
      return this.DataSyncService.isDataSyncInProgress;
    } else {
      this.LogService && this.LogService.warn(`InternalAnalyticsPublishingService no DataSyncService to query`);
      return false;
    }
  }

  get DataSyncService() {
    if (!this._DataSyncService && this.$injector) {
      try {
        this._DataSyncService = this.$injector.get('DataSyncService');
      } catch (e) {
        this.LogService && this.LogService.warn(`Error instantiating DataSyncService:`, e);
      }
    }
    return this._DataSyncService;
  }

  get shouldPublishQueue() {
    if (!this._queue || !this._queue.length) {
      return false;
    }
    if (this.isDataSyncInProgress) {
      return false;
    }
    if (this.DataSyncService && this.DataSyncService.staffUser) {
      // We have a logged-in user, so we should publish
      return true;
    }
    // No logged-in user; don't attempt to publish
    return false;
  }

  trackEvent(eventName, data = null) {
    const event = {
      name: eventName,
      captured_at: (new Date()).toISOString(),
      data: redactSensitiveValues(data),
    };
    this._queue.push(event);
    this.setNeedsPublish();
  }

  setCustomAttribute(name, value) {
    // Clearing an attribute must not become setting it to REDACTED.
    if (isSensitiveKey(name) && value !== null && value !== undefined) {
      assign(this._attributes, name, REDACTED);
      return;
    }
    assign(this._attributes, name, redactSensitiveValues(value));
  }

  publishQueue() {
    if (!this.shouldPublishQueue) {
      return 0;
    }
    const data = {
      "events": this._queue,
      "custom_attributes": this._attributes,
    };
    this._needsPublish = false;
    return this.APIService.post(analyticsPublishPath, data)
      .then(response => {
        this._queue = [];
      })
      .catch(error => {
        this.LogService.error(`InternalAnalyticsPublishingService got error:`, error);
        this._publishError = error;
        return this.$q.reject(error);
      })
  }

  setNeedsPublish() {
    this._needsPublish = true;
    if (!this._publishTimer) {
      const publishIfNeeded = () => {
        if (this._needsPublish && !this._publishError) {
          this.publishQueue();
        }
      };
      this._publishTimer = this.$interval(publishIfNeeded, logPublishIntervalSecs * 1000);
    }
  }
}

export const moduleName = 'recaps.services.InternalAnalyticsPublishingService';
export const module = angular.module(moduleName, [
  apiServiceModule.name,
  logServiceModule.name,
]);

module.service('InternalAnalyticsPublishingService', InternalAnalyticsPublishingService);

export default module;
